/**
 * Editor Visual de Relatórios v3
 * 
 * Abordagem: Renderização página-por-página com jsPDF + html2canvas
 * Cada página é renderizada individualmente garantindo controle total
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import {
  ArrowLeft,
  Sun,
  Moon,
  Monitor,
  Smartphone,
  Download,
  Save,
  FolderOpen,
  Trash2,
  Plus,
  GripVertical,
  FileDown,
  RotateCcw,
  Loader2,
  Check,
  Building2,
  FileText,
  ClipboardList,
  ZoomIn,
  ZoomOut,
  Mail,
  ChevronUp,
  ChevronDown,
  Copy,
  FileIcon,
  Layers,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Importa as seções reais do relatório
import {
  ImoveisSection,
  CenarioAvcbSection,
  CenarioAlvarasSection,
  DesempenhoSection,
} from '@/features/relatorios/components';

// ============================================================================
// TIPOS
// ============================================================================

interface SectionType {
  id: string;
  type: string;
  label: string;
  icon: React.ReactNode;
}

interface PageItem {
  id: string;
  type: string;
  height: number; // Altura em % (10-100)
}

interface Page {
  id: string;
  items: PageItem[];
}

interface SavedTemplate {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  orientation: 'portrait' | 'landscape';
  theme: 'light' | 'dark';
  pages: Page[];
}

// ============================================================================
// CONSTANTES
// ============================================================================

const SECTION_TYPES: SectionType[] = [
  { id: 'cover', type: 'cover', label: 'Capa', icon: <FileText className="h-4 w-4" /> },
  { id: 'imoveis', type: 'imoveis', label: 'Imóveis', icon: <Building2 className="h-4 w-4" /> },
  { id: 'cenario-avcb', type: 'cenario-avcb', label: 'Cenário AVCB', icon: <FileText className="h-4 w-4" /> },
  { id: 'cenario-alvaras', type: 'cenario-alvaras', label: 'Cenário Alvarás', icon: <FileText className="h-4 w-4" /> },
  { id: 'desempenho', type: 'desempenho', label: 'Desempenho', icon: <ClipboardList className="h-4 w-4" /> },
];

// A4 em mm
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

// A4 em pixels para preview (escala 1:1 a 96dpi)
const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;

// ============================================================================
// COMPONENTE DE CAPA
// ============================================================================

function CoverSection({ forPrint = false }: { forPrint?: boolean }) {
  return (
    <div className={`flex flex-col items-center justify-center h-full text-center ${forPrint ? 'p-12' : 'p-8'}`}>
      <div className="mb-6">
        <img
          src="/claro-logo.png"
          alt="Claro"
          className="h-16 mx-auto mb-6"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <h1 className={`font-bold mb-2 ${forPrint ? 'text-4xl' : 'text-3xl'}`}>Claro Gravity</h1>
        <p className={`text-muted-foreground ${forPrint ? 'text-xl' : 'text-lg'}`}>
          Sistema de Gestão de Legalizações
        </p>
      </div>
      <div className="w-32 h-px bg-border my-8" />
      <h2 className={`font-semibold mb-4 ${forPrint ? 'text-2xl' : 'text-xl'}`}>
        Relatório de Conformidade
      </h2>
      <p className="text-muted-foreground">
        {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
      </p>
    </div>
  );
}

// ============================================================================
// COMPONENTE DE ESPAÇO EM BRANCO
// ============================================================================

function BlankSection({ forPrint = false }: { forPrint?: boolean }) {
  if (forPrint) {
    return <div className="h-full" />;
  }
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-8 border-2 border-dashed border-muted-foreground/20 rounded-lg">
      <FileIcon className="h-12 w-12 text-muted-foreground/30 mb-4" />
      <p className="text-muted-foreground text-sm">Espaço em branco</p>
    </div>
  );
}

// ============================================================================
// RENDERIZADOR DE SEÇÃO
// ============================================================================

function RenderSection({ type, forPrint = false }: { type: string; forPrint?: boolean }) {
  switch (type) {
    case 'cover':
      return <CoverSection forPrint={forPrint} />;
    case 'imoveis':
      return <ImoveisSection />;
    case 'cenario-avcb':
      return <CenarioAvcbSection />;
    case 'cenario-alvaras':
      return <CenarioAlvarasSection />;
    case 'desempenho':
      return <DesempenhoSection />;
    case 'blank':
      return <BlankSection forPrint={forPrint} />;
    default:
      return null;
  }
}

// ============================================================================
// COMPONENTE DE ITEM EDITÁVEL
// ============================================================================

interface EditableItemProps {
  item: PageItem;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onResize: (height: number) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

function EditableItem({
  item,
  onRemove,
  onMoveUp,
  onMoveDown,
  onResize,
  canMoveUp,
  canMoveDown,
}: EditableItemProps) {
  const sectionType = SECTION_TYPES.find(s => s.type === item.type);

  return (
    <div
      className="relative bg-card rounded-lg overflow-hidden border border-border hover:ring-2 hover:ring-primary/30 transition-all"
      style={{ height: `${item.height}%`, minHeight: '60px' }}
    >
      {/* Toolbar */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-muted/90 backdrop-blur-sm flex items-center justify-between px-2 z-20 border-b">
        <div className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-medium">
            {item.type === 'blank' ? 'Em branco' : sectionType?.label}
          </span>
          <Badge variant="outline" className="text-[10px] h-5">
            {item.height}%
          </Badge>
        </div>
        <div className="flex items-center gap-0.5">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => onResize(Math.max(10, item.height - 10))}
                  disabled={item.height <= 10}
                >
                  <ZoomOut className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">-10%</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => onResize(Math.min(100, item.height + 10))}
                  disabled={item.height >= 100}
                >
                  <ZoomIn className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">+10%</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Separator orientation="vertical" className="h-4 mx-1" />

          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={onMoveUp}
                  disabled={!canMoveUp}
                >
                  <ChevronUp className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Mover ↑</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={onMoveDown}
                  disabled={!canMoveDown}
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Mover ↓</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Separator orientation="vertical" className="h-4 mx-1" />

          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:text-destructive"
                  onClick={onRemove}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Remover</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="h-full pt-8 overflow-auto">
        <div className="p-3 h-full">
          <RenderSection type={item.type} />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENTE DE PÁGINA EDITÁVEL
// ============================================================================

interface EditablePageProps {
  page: Page;
  pageIndex: number;
  totalPages: number;
  isDarkMode: boolean;
  isLandscape: boolean;
  zoom: number;
  onUpdate: (page: Page) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onAddSection: (type: string) => void;
}

function EditablePage({
  page,
  pageIndex,
  totalPages,
  isDarkMode,
  isLandscape,
  zoom,
  onUpdate,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onAddSection,
}: EditablePageProps) {
  const width = (isLandscape ? A4_HEIGHT_PX : A4_WIDTH_PX) * zoom;
  const height = (isLandscape ? A4_WIDTH_PX : A4_HEIGHT_PX) * zoom;

  const themeClasses = isDarkMode
    ? 'bg-zinc-900 text-zinc-100'
    : 'bg-white text-zinc-900';

  const handleRemoveItem = (itemId: string) => {
    onUpdate({ ...page, items: page.items.filter(i => i.id !== itemId) });
  };

  const handleMoveItem = (idx: number, dir: 'up' | 'down') => {
    const items = [...page.items];
    const targetIdx = dir === 'up' ? idx - 1 : idx + 1;
    [items[idx], items[targetIdx]] = [items[targetIdx], items[idx]];
    onUpdate({ ...page, items });
  };

  const handleResizeItem = (itemId: string, newHeight: number) => {
    onUpdate({
      ...page,
      items: page.items.map(i => i.id === itemId ? { ...i, height: newHeight } : i)
    });
  };

  return (
    <div className="relative group">
      {/* Header da página */}
      <div className="absolute -top-8 left-0 right-0 flex items-center justify-between px-2">
        <span className="text-sm font-medium text-muted-foreground">
          Página {pageIndex + 1} / {totalPages}
        </span>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onMoveUp} disabled={pageIndex === 0}>
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onMoveDown} disabled={pageIndex === totalPages - 1}>
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onDuplicate}>
            <Copy className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={onDelete} disabled={totalPages <= 1}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Página */}
      <div
        className={`relative shadow-2xl transition-all ${themeClasses} ring-2 ring-primary/10`}
        style={{ width, height, padding: 16 * zoom }}
      >
        <div className="h-full flex flex-col gap-2">
          {page.items.length > 0 ? (
            page.items.map((item, idx) => (
              <EditableItem
                key={item.id}
                item={item}
                onRemove={() => handleRemoveItem(item.id)}
                onMoveUp={() => handleMoveItem(idx, 'up')}
                onMoveDown={() => handleMoveItem(idx, 'down')}
                onResize={(h) => handleResizeItem(item.id, h)}
                canMoveUp={idx > 0}
                canMoveDown={idx < page.items.length - 1}
              />
            ))
          ) : (
            <div className="flex-1 flex items-center justify-center border-2 border-dashed border-muted-foreground/20 rounded-lg">
              <div className="text-center">
                <Plus className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground text-sm">Página vazia</p>
                <p className="text-muted-foreground/50 text-xs mt-1">Clique + para adicionar</p>
              </div>
            </div>
          )}
        </div>

        {/* Botão adicionar */}
        <div className="absolute bottom-3 right-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="rounded-full shadow-lg h-8 px-3">
                <Plus className="h-4 w-4 mr-1" />
                Adicionar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {SECTION_TYPES.map((s) => (
                <DropdownMenuItem key={s.id} onClick={() => onAddSection(s.type)}>
                  {s.icon}
                  <span className="ml-2">{s.label}</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onAddSection('blank')}>
                <FileIcon className="h-4 w-4" />
                <span className="ml-2">Espaço em branco</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function EditorRelatorio() {
  const navigate = useNavigate();
  const printContainerRef = useRef<HTMLDivElement>(null);

  // Estados
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [zoom, setZoom] = useState(0.55);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingProgress, setGeneratingProgress] = useState(0);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Páginas
  const [pages, setPages] = useState<Page[]>([
    { id: 'p1', items: [{ id: 'i1', type: 'cover', height: 100 }] },
    { id: 'p2', items: [{ id: 'i2', type: 'imoveis', height: 100 }] },
    { id: 'p3', items: [{ id: 'i3', type: 'cenario-avcb', height: 100 }] },
    { id: 'p4', items: [{ id: 'i4', type: 'cenario-alvaras', height: 100 }] },
    { id: 'p5', items: [{ id: 'i5', type: 'desempenho', height: 100 }] },
  ]);

  // Templates
  const [savedTemplates, setSavedTemplates] = useState<SavedTemplate[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);

  // Suprime erros ResizeObserver
  useEffect(() => {
    const orig = console.error;
    console.error = (...args) => {
      if (args[0]?.toString?.().includes?.('ResizeObserver')) return;
      orig.apply(console, args);
    };
    return () => { console.error = orig; };
  }, []);

  // Carrega templates
  useEffect(() => {
    const saved = localStorage.getItem('report-templates-v3');
    if (saved) {
      try { setSavedTemplates(JSON.parse(saved)); } catch {}
    }
  }, []);

  // Tema
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  // Helpers
  const genId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const resetPdf = () => {
    setPdfBlob(null);
    setShowPreview(false);
  };

  // Operações de página
  const addBlankPage = () => {
    setPages(p => [...p, { id: `p-${genId()}`, items: [] }]);
    resetPdf();
  };

  const addPageWithSection = (type: string) => {
    setPages(p => [...p, {
      id: `p-${genId()}`,
      items: [{ id: `i-${genId()}`, type, height: 100 }]
    }]);
    resetPdf();
  };

  const updatePage = (pageId: string, updated: Page) => {
    setPages(p => p.map(pg => pg.id === pageId ? updated : pg));
    resetPdf();
  };

  const deletePage = (pageId: string) => {
    if (pages.length <= 1) return;
    setPages(p => p.filter(pg => pg.id !== pageId));
    resetPdf();
  };

  const duplicatePage = (pageId: string) => {
    const pg = pages.find(p => p.id === pageId);
    if (!pg) return;
    const idx = pages.findIndex(p => p.id === pageId);
    const newPages = [...pages];
    newPages.splice(idx + 1, 0, {
      id: `p-${genId()}`,
      items: pg.items.map(i => ({ ...i, id: `i-${genId()}` }))
    });
    setPages(newPages);
    resetPdf();
  };

  const movePage = (pageId: string, dir: 'up' | 'down') => {
    const idx = pages.findIndex(p => p.id === pageId);
    const targetIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= pages.length) return;
    const newPages = [...pages];
    [newPages[idx], newPages[targetIdx]] = [newPages[targetIdx], newPages[idx]];
    setPages(newPages);
    resetPdf();
  };

  const addSectionToPage = (pageId: string, type: string) => {
    setPages(p => p.map(pg => {
      if (pg.id !== pageId) return pg;
      const usedH = pg.items.reduce((s, i) => s + i.height, 0);
      let items = [...pg.items];
      let newH = 30;
      if (usedH + newH > 100) {
        const red = (usedH + newH - 100) / items.length;
        items = items.map(i => ({ ...i, height: Math.max(10, i.height - red) }));
      }
      return { ...pg, items: [...items, { id: `i-${genId()}`, type, height: newH }] };
    }));
    resetPdf();
  };

  const resetToDefault = () => {
    setPages([
      { id: 'p1', items: [{ id: 'i1', type: 'cover', height: 100 }] },
      { id: 'p2', items: [{ id: 'i2', type: 'imoveis', height: 100 }] },
      { id: 'p3', items: [{ id: 'i3', type: 'cenario-avcb', height: 100 }] },
      { id: 'p4', items: [{ id: 'i4', type: 'cenario-alvaras', height: 100 }] },
      { id: 'p5', items: [{ id: 'i5', type: 'desempenho', height: 100 }] },
    ]);
    resetPdf();
  };

  // Templates
  const saveTemplate = () => {
    if (!templateName.trim()) return;
    const tmpl: SavedTemplate = {
      id: genId(),
      name: templateName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      orientation: isLandscape ? 'landscape' : 'portrait',
      theme: isDarkMode ? 'dark' : 'light',
      pages,
    };
    const updated = [...savedTemplates, tmpl];
    setSavedTemplates(updated);
    localStorage.setItem('report-templates-v3', JSON.stringify(updated));
    setShowSaveDialog(false);
    setTemplateName('');
  };

  const loadTemplate = (t: SavedTemplate) => {
    setIsLandscape(t.orientation === 'landscape');
    setIsDarkMode(t.theme === 'dark');
    setPages(t.pages);
    setShowLoadDialog(false);
    resetPdf();
  };

  const deleteTemplate = (id: string) => {
    const updated = savedTemplates.filter(t => t.id !== id);
    setSavedTemplates(updated);
    localStorage.setItem('report-templates-v3', JSON.stringify(updated));
  };

  // =========================================================================
  // GERAÇÃO DE PDF - Página por página
  // =========================================================================
  const generatePDF = useCallback(async () => {
    const container = printContainerRef.current;
    if (!container) return;

    setIsGenerating(true);
    setGeneratingProgress(0);

    try {
      // Dimensões do PDF em mm
      const pdfWidth = isLandscape ? A4_HEIGHT_MM : A4_WIDTH_MM;
      const pdfHeight = isLandscape ? A4_WIDTH_MM : A4_HEIGHT_MM;

      // Cria o PDF
      const pdf = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      // Renderiza cada página individualmente
      const pageElements = container.querySelectorAll('.pdf-page');
      
      for (let i = 0; i < pageElements.length; i++) {
        const pageEl = pageElements[i] as HTMLElement;
        
        // Atualiza progresso
        setGeneratingProgress(Math.round(((i + 0.5) / pageElements.length) * 100));

        // Renderiza para canvas
        const canvas = await html2canvas(pageEl, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: isDarkMode ? '#18181b' : '#ffffff',
          width: pageEl.offsetWidth,
          height: pageEl.offsetHeight,
        });

        // Adiciona ao PDF
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        
        if (i > 0) {
          pdf.addPage();
        }

        // Preenche a página inteira
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
        
        setGeneratingProgress(Math.round(((i + 1) / pageElements.length) * 100));
      }

      // Gera blob
      const blob = pdf.output('blob');
      setPdfBlob(blob);
      setShowPreview(true);

    } catch (error) {
      console.error('[PDF] Erro:', error);
      alert('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setIsGenerating(false);
      setGeneratingProgress(0);
    }
  }, [pages, isDarkMode, isLandscape]);

  const downloadPDF = () => {
    if (!pdfBlob) return;
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-${new Date().toISOString().split('T')[0]}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const emailPDF = () => {
    if (!pdfBlob) return;
    downloadPDF();
    setTimeout(() => {
      const subject = encodeURIComponent('Relatório Claro Gravity');
      const body = encodeURIComponent('Segue em anexo o relatório de conformidade.');
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }, 500);
  };

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <div className="min-h-screen bg-muted/30">
      {/* Toolbar */}
      <div className="sticky top-0 z-50 bg-background border-b shadow-sm">
        <div className="container max-w-full px-4 py-2">
          <div className="flex items-center justify-between gap-4">
            {/* Esquerda */}
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/relatorio')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="font-semibold">Editor de Relatório</h1>
                <p className="text-xs text-muted-foreground">
                  {pages.length} página(s) • {isLandscape ? 'Paisagem' : 'Retrato'}
                </p>
              </div>
            </div>

            {/* Centro */}
            <div className="flex items-center gap-4">
              {/* Zoom */}
              <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setZoom(z => Math.max(0.3, z - 0.1))}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium w-12 text-center">{Math.round(zoom * 100)}%</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setZoom(z => Math.min(1, z + 0.1))}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>

              {/* Orientação */}
              <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-1">
                <Smartphone className={`h-4 w-4 ${!isLandscape ? 'text-primary' : 'text-muted-foreground'}`} />
                <Switch checked={isLandscape} onCheckedChange={(v) => { setIsLandscape(v); resetPdf(); }} />
                <Monitor className={`h-4 w-4 ${isLandscape ? 'text-primary' : 'text-muted-foreground'}`} />
              </div>

              {/* Tema */}
              <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-1">
                <Sun className={`h-4 w-4 ${!isDarkMode ? 'text-amber-500' : 'text-muted-foreground'}`} />
                <Switch checked={isDarkMode} onCheckedChange={(v) => { setIsDarkMode(v); resetPdf(); }} />
                <Moon className={`h-4 w-4 ${isDarkMode ? 'text-blue-500' : 'text-muted-foreground'}`} />
              </div>
            </div>

            {/* Direita */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowLoadDialog(true)}>
                <FolderOpen className="h-4 w-4 mr-1" />
                Carregar
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowSaveDialog(true)}>
                <Save className="h-4 w-4 mr-1" />
                Salvar
              </Button>

              <Separator orientation="vertical" className="h-6" />

              <Button variant="outline" size="sm" onClick={resetToDefault}>
                <RotateCcw className="h-4 w-4 mr-1" />
                Reset
              </Button>

              <Separator orientation="vertical" className="h-6" />

              {!pdfBlob ? (
                <Button onClick={generatePDF} disabled={isGenerating || pages.length === 0}>
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      {generatingProgress}%
                    </>
                  ) : (
                    <>
                      <FileDown className="h-4 w-4 mr-1" />
                      Gerar PDF
                    </>
                  )}
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-emerald-600 border-emerald-300 bg-emerald-50">
                    <Check className="h-3 w-3 mr-1" />
                    Pronto
                  </Badge>
                  <Button size="sm" onClick={() => setShowPreview(true)}>
                    <Eye className="h-4 w-4 mr-1" />
                    Preview
                  </Button>
                  <Button onClick={downloadPDF} size="sm">
                    <Download className="h-4 w-4 mr-1" />
                    Baixar
                  </Button>
                  <Button onClick={emailPDF} variant="outline" size="sm">
                    <Mail className="h-4 w-4 mr-1" />
                    Email
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-background border-r min-h-[calc(100vh-57px)] sticky top-[57px] flex flex-col">
          <div className="p-4 border-b">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Páginas
            </h3>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={addBlankPage}>
                <Plus className="h-4 w-4 mr-2" />
                Página em branco
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <FileText className="h-4 w-4 mr-2" />
                    Página com seção
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {SECTION_TYPES.map((s) => (
                    <DropdownMenuItem key={s.id} onClick={() => addPageWithSection(s.type)}>
                      {s.icon}
                      <span className="ml-2">{s.label}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <ScrollArea className="flex-1 p-4">
            <div className="space-y-3">
              {pages.map((page, idx) => (
                <div
                  key={page.id}
                  className="p-2 border rounded-lg bg-card hover:border-primary/50 cursor-pointer transition-colors"
                  onClick={() => {
                    document.getElementById(`edit-page-${page.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium">Página {idx + 1}</span>
                    <Badge variant="outline" className="text-[10px]">{page.items.length} item(s)</Badge>
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {page.items.length === 0
                      ? 'Vazia'
                      : page.items.map(i => SECTION_TYPES.find(s => s.type === i.type)?.label || 'Blank').join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          <div className="p-4 border-t text-xs text-muted-foreground">
            <p className="font-medium mb-1">💡 Dicas:</p>
            <ul className="space-y-1">
              <li>• Clique na página para navegar</li>
              <li>• Use o botão <strong>+</strong> para adicionar seções</li>
              <li>• Ajuste altura com <strong>-/+</strong></li>
            </ul>
          </div>
        </div>

        {/* Canvas Editor */}
        <div className="flex-1 p-8 overflow-auto" style={{ minHeight: 'calc(100vh - 57px)' }}>
          <div className="flex flex-col items-center gap-10">
            {pages.map((page, idx) => (
              <div key={page.id} id={`edit-page-${page.id}`}>
                <EditablePage
                  page={page}
                  pageIndex={idx}
                  totalPages={pages.length}
                  isDarkMode={isDarkMode}
                  isLandscape={isLandscape}
                  zoom={zoom}
                  onUpdate={(p) => updatePage(page.id, p)}
                  onDelete={() => deletePage(page.id)}
                  onDuplicate={() => duplicatePage(page.id)}
                  onMoveUp={() => movePage(page.id, 'up')}
                  onMoveDown={() => movePage(page.id, 'down')}
                  onAddSection={(t) => addSectionToPage(page.id, t)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ================================================================== */}
      {/* CONTAINER DE IMPRESSÃO OCULTO - Renderiza páginas limpas           */}
      {/* ================================================================== */}
      <div
        ref={printContainerRef}
        style={{
          position: 'fixed',
          left: '-9999px',
          top: 0,
          width: isLandscape ? A4_HEIGHT_PX : A4_WIDTH_PX,
          visibility: 'hidden',
        }}
      >
        {pages.map((page) => (
          <div
            key={`print-${page.id}`}
            className={`pdf-page ${isDarkMode ? 'bg-zinc-900 text-zinc-100' : 'bg-white text-zinc-900'}`}
            style={{
              width: isLandscape ? A4_HEIGHT_PX : A4_WIDTH_PX,
              height: isLandscape ? A4_WIDTH_PX : A4_HEIGHT_PX,
              padding: 24,
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {page.items.length > 0 ? (
                page.items.map((item) => (
                  <div
                    key={`print-${item.id}`}
                    style={{
                      height: `${item.height}%`,
                      overflow: 'hidden',
                    }}
                  >
                    <RenderSection type={item.type} forPrint />
                  </div>
                ))
              ) : (
                <div style={{ flex: 1 }} />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Dialog: Salvar Template */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Salvar Template</DialogTitle>
            <DialogDescription>Salve o layout atual para reutilizar</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="template-name">Nome do Template</Label>
            <Input
              id="template-name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Ex: Relatório Mensal"
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancelar</Button>
            <Button onClick={saveTemplate} disabled={!templateName.trim()}>
              <Save className="h-4 w-4 mr-2" />Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Carregar Template */}
      <Dialog open={showLoadDialog} onOpenChange={setShowLoadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Carregar Template</DialogTitle>
            <DialogDescription>Selecione um template salvo</DialogDescription>
          </DialogHeader>
          <div className="py-4 max-h-80 overflow-auto">
            {savedTemplates.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <FolderOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum template salvo</p>
              </div>
            ) : (
              <div className="space-y-2">
                {savedTemplates.map((t) => (
                  <div key={t.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                    <div className="flex-1 cursor-pointer" onClick={() => loadTemplate(t)}>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {t.pages.length} páginas • {t.orientation === 'landscape' ? 'Paisagem' : 'Retrato'}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="hover:text-destructive" onClick={() => deleteTemplate(t.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoadDialog(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Preview PDF */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Preview do PDF</DialogTitle>
            <DialogDescription>Verifique o documento antes de baixar</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-muted rounded-lg" style={{ height: '70vh' }}>
            {pdfBlob && (
              <iframe
                src={URL.createObjectURL(pdfBlob)}
                className="w-full h-full"
                title="PDF Preview"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Fechar</Button>
            <Button onClick={downloadPDF}>
              <Download className="h-4 w-4 mr-2" />
              Baixar PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
